import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import createAction, { ActionPayload } from 'core/redux/helpers/createAction';
import createReducer from 'core/redux/helpers/createReducer';
import { addEventListener, closest, removeEventListener } from 'dom-helpers';
import { useSelector } from 'react-redux';
import { selectDeviceType } from '../../../core/redux/reducers';

const initialState = {
  activeFilter: '',
};

export type IFilterState = typeof initialState;
type FilterStateApi = {
  filterState: IFilterState;
  filterDispatch?: React.Dispatch<ActionPayload<any>>;
};

export const FilterContext = createContext<FilterStateApi>({
  filterState: initialState,
});

export const activeFilterToggled = createAction<
  | string
  | {
      filter: string;
      toggle?: boolean;
    }
>('activeFilterToggled', payload => {
  return {
    payload,
  };
});

const reducer = createReducer<IFilterState>(initialState, {
  [activeFilterToggled]: function (
    state: IFilterState,
    { payload }: ReturnType<typeof activeFilterToggled>,
  ) {
    const filter = typeof payload === 'string' ? payload : payload.filter;
    const toggle = typeof payload === 'string' ? true : payload.toggle;

    if (toggle && state.activeFilter === filter) {
      state.activeFilter = '';
    } else {
      state.activeFilter = filter;
    }

    return state;
  },
});

export const FilterStateProvider = (props: { children: React.ReactNode }) => {
  const reducerCache = useMemo(() => reducer, []);
  const [filterState, filterDispatch] = useReducer(reducerCache, initialState);
  const deviceType = useSelector(selectDeviceType);

  useEffect(() => {
    function handleEvent(e: Event) {
      const isOutsideDropdown = !closest(e.target as Element, '.dropdown');
      const isMobile = deviceType === 'mobile';

      const autoHideWhenClickOutsideSearch =
        isOutsideDropdown && isMobile && filterState.activeFilter === 'search';

      const autoHideWhenClickOutsidePerson =
        isOutsideDropdown && filterState.activeFilter === 'person';

      const autoHideWhenClickOutsideSort =
        isOutsideDropdown && filterState.activeFilter === 'sort';

      if (
        autoHideWhenClickOutsideSearch ||
        autoHideWhenClickOutsidePerson ||
        autoHideWhenClickOutsideSort
      ) {
        filterDispatch(activeFilterToggled(''));
      }
    }

    addEventListener(document.body, 'click', handleEvent);

    return () => {
      removeEventListener(document.body, 'click', handleEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceType, filterState.activeFilter]);

  return (
    <FilterContext.Provider value={{ filterState, filterDispatch }}>
      {props.children}
    </FilterContext.Provider>
  );
};

export const useFilterState = () => {
  return useContext(FilterContext);
};
